import { useEffect, useRef } from 'react';
import { useNavigationStore, type SectionId } from '@/stores/navigationStore';

const SECTIONS = [
  'home',
  'how-i-work',
  'case-studies',
  'skills',
  'about-me',
  'footer',
] as const;

export function useSectionTracker() {
  const setActiveSection = useNavigationStore((state) => state.setActiveSection);
  const isNavigating = useNavigationStore((state) => state.isNavigating);

  // Ref so the observer callback always sees the current navigation state.
  const isNavigatingRef = useRef(isNavigating);
  useEffect(() => {
    isNavigatingRef.current = isNavigating;
  }, [isNavigating]);

  useEffect(() => {
    // Track how many vertical pixels of each section are actually visible in the
    // viewport, and treat the section occupying the MOST of the viewport as
    // active. This is stable where the previous "whichever entry fired last while
    // >10% visible" logic was not:
    //   - at section boundaries (two sections visible at once), and
    //   - while a section is pinned (`position: fixed`) by GSAP — the pinned
    //     Case Studies section fills the viewport for its whole pin, so it stays
    //     the most-visible section and the nav tracks it instead of flickering.
    const visibleHeight = new Map<string, number>();
    let lastActive: string | null = null;

    const pickActive = () => {
      if (isNavigatingRef.current) return;

      let bestId: string | null = null;
      let bestPx = 0;
      for (const id of SECTIONS) {
        const px = visibleHeight.get(id) ?? 0;
        if (px > bestPx) {
          bestPx = px;
          bestId = id;
        }
      }

      if (!bestId || bestId === lastActive) return;
      lastActive = bestId;
      // Footer clears the active state so no nav tab stays highlighted at the end.
      setActiveSection(bestId === 'footer' ? null : (bestId as SectionId));
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibleHeight.set(
            entry.target.id,
            entry.isIntersecting ? entry.intersectionRect.height : 0,
          );
        }
        pickActive();
      },
      {
        // Fine-grained thresholds so visible height updates smoothly while scrolling.
        threshold: Array.from({ length: 21 }, (_, i) => i / 20),
      },
    );

    // Observe after first paint so every section exists in the DOM.
    const raf = requestAnimationFrame(() => {
      SECTIONS.forEach((id) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    });

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [setActiveSection]);
}
